//@ts-nocheck

'use client'

import React, { useEffect, useRef } from 'react'

import * as S from './StoreLocator.styles'

import Locator, { Map, Search } from '@dynamo-caffeina-dev/locator-frontend'
import '@dynamo-caffeina-dev/locator-frontend/styles'
import Icon from '../../atoms/Icon/Icon.component'
import { Paragraph, Heading } from '../../atoms/Typography/Typography.component'
import { SpacerBase } from '../../atoms/GridSystem'
import InfoWindow from './InfoWindow/InfoWindow.component'
import { useTranslations } from 'next-intl'

import { Container } from '../../atoms/GridSystem'
import { useTheme } from 'styled-components'

const config = {
  mapProvider: {
    name: 'google',
    APIKey: process.env.NEXT_PUBLIC_MAPS_API_KEY,
    config: {
      region: 'IT',
      language: 'it',
      libraries: ['places', 'geocoding'],
    },
  },
  apiUrl: process.env.NEXT_PUBLIC_STORE_LOCATOR_API_URL,
  mapId: process.env.NEXT_PUBLIC_STORE_LOCATOR_MAP_ID,
  endpoints: {
    clusters: {
      method: 'GET',
      path: '/maps/:mapId',
    },
    markers: {
      method: 'GET',
      path: '/maps/:mapId/clusters/:clusterId',
    },
    find: {
      method: 'POST',
      path: '/maps/:mapId/find',
    },
    allMarkers: {
      method: 'GET',
      path: '/maps/:mapId/allMarkers',
    },
  },
}

const StoreLocator = ({ mapId, title }) => {
  const t = useTranslations()
  const theme = useTheme()

  const locRootRef = useRef<HTMLDivElement | null>(null)
  const activeIndexRef = useRef<number>(-1)
  const q = (sel: string, root: HTMLElement) =>
    root.querySelector(sel) as HTMLElement | null
  const qa = (sel: string, root: HTMLElement) =>
    Array.from(root.querySelectorAll(sel)) as HTMLElement[]
  const getFocusable = (root: HTMLElement) =>
    qa(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
      root
    ).filter(
      (el) =>
        !el.hasAttribute('disabled') &&
        !el.getAttribute('aria-hidden') &&
        !el.hidden
    )

  useEffect(() => {
    const styleElement = document.createElement('style')
    styleElement.textContent = `
        :root {
          --dyn-locator__colors-accent-500: ${theme.palette.green['200']} !important;
          --button-color: ${theme.palette.grayscale['50']};
          --dyn-locator__colors-accent-600: ${theme.palette.green['900']};;
          --dyn-locator__low-density-cluster-color: ${theme.palette.greenHill['600']};
          --dyn-locator__low-density-cluster-text-color: ${theme.palette.grayscale['50']};
          --dyn-locator__high-density-cluster-color: ${theme.palette.greenHill['950']};
          --dyn-locator__high-density-cluster-text-color: ${theme.palette.grayscale['50']};
        }
        /* Focus ring chiaro solo via tastiera */
        [data-dyn-a11y] [data-role="combobox"]:focus-visible,
        [data-dyn-a11y] [data-role="clear"]:focus-visible,
        [data-dyn-a11y] [role="option"]:focus-visible {
          outline: 2px solid currentColor;
          outline-offset: 3px;
        }
        /* Evidenza opzione attiva/selezionata */
        [data-dyn-a11y] [role="option"][aria-selected="true"],
        [data-dyn-a11y] [role="option"].is-active {
          background: rgba(0,0,0,0.06);
        }
        /* live region nascosta visivamente */
        [data-dyn-a11y] .sr-only {
          position:absolute !important; width:1px; height:1px; overflow:hidden;
          clip:rect(1px,1px,1px,1px); white-space:nowrap;
        }
      `
    document.head.appendChild(styleElement)
    return () => {
      try {
        document.head.removeChild(styleElement)
      } catch {}
    }
  }, [])

  useEffect(() => {
    const root = locRootRef.current
    let selectingProgrammatically = false
    if (!root) return

    root.setAttribute('data-dyn-a11y', 'true')

    // live region
    let live = q('#locator-status', root)
    if (!live) {
      live = document.createElement('div')
      live.id = 'locator-status'
      live.className = 'sr-only'
      live.setAttribute('aria-live', 'polite')
      root.appendChild(live)
    }
    const announce = (msg: string) => {
      if (live) live.textContent = msg
    }

    const safeSetAttr = (el: Element, name: string, value: string) => {
      if (el.getAttribute(name) !== value) el.setAttribute(name, value)
    }

    const ensureRoles = () => {
      const input = root.querySelector(
        'input[name="locator_search_field"]'
      ) as HTMLInputElement | null
      if (input) {
        safeSetAttr(input, 'id', 'locator-input')
        safeSetAttr(input, 'role', 'combobox')
        safeSetAttr(input, 'aria-autocomplete', 'list')
        safeSetAttr(input, 'data-role', 'combobox')
        if (!input.hasAttribute('aria-expanded'))
          safeSetAttr(input, 'aria-expanded', 'false')
        safeSetAttr(input, 'aria-controls', 'locator-listbox')

        const clearBtn = root.querySelector(
          'button[aria-label], button._clearButton_1vbtg_10'
        ) as HTMLButtonElement | null
        if (clearBtn) {
          const label =
            (t('storeLocator.clearLabel', { default: 'Clear' }) as string) ||
            'Clear'
          safeSetAttr(clearBtn, 'aria-label', label)
          safeSetAttr(clearBtn, 'data-role', 'clear')
        }

        const listbox = (root.querySelector(
          '._predictionsContainer_1vbtg_18'
        ) ||
          root.querySelector('[data-predictions-container]') ||
          root.querySelector('[role="listbox"]')) as HTMLElement | null

        if (listbox) {
          safeSetAttr(listbox, 'id', 'locator-listbox')
          safeSetAttr(listbox, 'role', 'listbox')

          const items = qa(
            '._predictionItem_1vbtg_29, [data-prediction-item]',
            listbox
          )
          items.forEach((el, i) => {
            safeSetAttr(el, 'role', 'option')
            if (!el.id) el.id = `locator-opt-${i}`
            safeSetAttr(el, 'tabindex', '-1')
          })

          const dropdown = listbox.closest(
            '._dropdown_1nmik_166'
          ) as HTMLElement | null
          if (dropdown) {
            const isOpen = input.getAttribute('aria-expanded') === 'true'
            const isHidden = !isOpen
            if (dropdown.hidden !== isHidden) dropdown.hidden = isHidden
            const desiredDisplay = isHidden ? 'none' : ''
            if ((dropdown.style.display || '') !== desiredDisplay) {
              dropdown.style.display = desiredDisplay
            }
          }
        }
      }
    }

    const setOpen = (open: boolean) => {
      const input = root.querySelector(
        '#locator-input'
      ) as HTMLInputElement | null
      const listbox = root.querySelector(
        '#locator-listbox'
      ) as HTMLElement | null
      const dropdown = listbox?.closest(
        '._dropdown_1nmik_166'
      ) as HTMLElement | null
      if (input) safeSetAttr(input, 'aria-expanded', open ? 'true' : 'false')
      if (dropdown) {
        const isHidden = !open
        if (dropdown.hidden !== isHidden) dropdown.hidden = isHidden
        const desiredDisplay = isHidden ? 'none' : ''
        if ((dropdown.style.display || '') !== desiredDisplay)
          dropdown.style.display = desiredDisplay
      }
      if (!open && input) {
        input.removeAttribute('aria-activedescendant')
        activeIndexRef.current = -1
        qa('[role="option"]', listbox || root).forEach((el) => {
          el.classList.remove('is-active')
          el.removeAttribute('aria-selected')
        })
      }
    }

    const setActiveByIndex = (idx: number) => {
      const input = root.querySelector(
        '#locator-input'
      ) as HTMLInputElement | null
      const listbox = root.querySelector(
        '#locator-listbox'
      ) as HTMLElement | null
      if (!input || !listbox) return
      const opts = qa('[role="option"]', listbox)
      if (!opts.length) return
      const clamped = Math.max(0, Math.min(idx, opts.length - 1))
      activeIndexRef.current = clamped
      opts.forEach((el) => {
        el.classList.remove('is-active')
        el.removeAttribute('aria-selected')
      })
      const el = opts[clamped]
      el.classList.add('is-active')
      el.setAttribute('aria-selected', 'true')
      safeSetAttr(input, 'aria-activedescendant', el.id)
      el.scrollIntoView({ block: 'nearest' })
    }

    const selectOption = (el: HTMLElement) => {
      const input = root.querySelector(
        '#locator-input'
      ) as HTMLInputElement | null
      const listbox = root.querySelector(
        '#locator-listbox'
      ) as HTMLElement | null
      if (!input) return

      const text = (el.textContent || '').trim()

      // Evita re-entrancy
      if (selectingProgrammatically) return
      selectingProgrammatically = true

      // 1) prova percorso "nativo" della libreria: click sull'opzione
      try {
        el.click()
      } catch {}

      // 2) lascia un tick per far reagire la libreria
      setTimeout(() => {
        const stillExpanded = input.getAttribute('aria-expanded') === 'true'
        const valueMatches = input.value.trim() === text

        if (!stillExpanded || valueMatches) {
          // la libreria ha gestito la selezione
          if (stillExpanded) setOpen(false)
          input.focus({ preventScroll: true })
          selectingProgrammatically = false
          return
        }

        // 3) fallback "manuale"
        input.value = text
        announce(
          `${t('storeLocator.selected', { default: 'Selected' })}: ${text}`
        )
        input.dispatchEvent(
          new InputEvent('input', { bubbles: true, composed: true })
        )
        input.dispatchEvent(new Event('change', { bubbles: true }))
        input.dispatchEvent(
          new KeyboardEvent('keydown', {
            key: 'Enter',
            code: 'Enter',
            bubbles: true,
          })
        )

        const confirmBtn =
          (root.querySelector(
            '[aria-label="Search"]'
          ) as HTMLButtonElement | null) ||
          (root.querySelector(
            'button[type="submit"]'
          ) as HTMLButtonElement | null)
        if (confirmBtn) setTimeout(() => confirmBtn.click(), 0)

        setOpen(false)
        input.focus({ preventScroll: true })
        selectingProgrammatically = false
      }, 0)
    }

    const onInputKeyDown = (e: KeyboardEvent) => {
      const input = e.currentTarget as HTMLInputElement
      const listbox = root.querySelector(
        '#locator-listbox'
      ) as HTMLElement | null
      const opts = listbox ? qa('[role="option"]', listbox) : []
      const hasOptions = opts.length > 0
      const expanded = input.getAttribute('aria-expanded') === 'true'

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          if (!expanded) setOpen(true)
          if (hasOptions) setActiveByIndex(activeIndexRef.current + 1)
          break
        case 'ArrowUp':
          e.preventDefault()
          if (!expanded) setOpen(true)
          if (hasOptions) {
            const next =
              activeIndexRef.current === -1
                ? opts.length - 1
                : activeIndexRef.current - 1
            setActiveByIndex(next)
          }
          break
        case 'Enter':
          if (expanded && activeIndexRef.current >= 0 && hasOptions) {
            e.preventDefault()
            selectOption(opts[activeIndexRef.current])
          }
          break
        case 'Escape':
          e.preventDefault()
          setOpen(false)
          break
        default:
          setOpen(true)
          break
      }
    }

    const onListboxClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest(
        '[role="option"]'
      ) as HTMLElement | null
      if (target) selectOption(target)
    }

    const onClearClick = () => {
      const input = root.querySelector(
        '#locator-input'
      ) as HTMLInputElement | null
      if (!input) return
      input.value = ''
      announce(
        t('storeLocator.cleared', { default: 'Input cleared' }) as string
      )
      activeIndexRef.current = -1
      input.focus({ preventScroll: true })
      setOpen(true)
    }

    const bindHandlersIfNeeded = () => {
      const input = root.querySelector(
        '#locator-input'
      ) as HTMLInputElement | null
      if (input && !(input as any).__dynA11yBound) {
        input.addEventListener('keydown', onInputKeyDown)
        input.addEventListener('focus', () => setOpen(true))
        ;(input as any).__dynA11yBound = true
      }
      const listbox = root.querySelector(
        '#locator-listbox'
      ) as HTMLElement | null
      if (listbox && !(listbox as any).__dynA11yBound) {
        listbox.addEventListener('mousedown', (e) => e.preventDefault())
        listbox.addEventListener('click', onListboxClick)
        ;(listbox as any).__dynA11yBound = true
      }
      const clearBtn = root.querySelector(
        '[data-role="clear"]'
      ) as HTMLButtonElement | null
      if (clearBtn && !(clearBtn as any).__dynA11yBound) {
        clearBtn.addEventListener('click', onClearClick)
        ;(clearBtn as any).__dynA11yBound = true
      }
    }

    const observer = new MutationObserver(() => {
      ensureRoles()
      bindHandlersIfNeeded()
    })

    // 👇 SOLO childList/subtree
    observer.observe(root, { childList: true, subtree: true })

    // init
    ensureRoles()
    bindHandlersIfNeeded()

    const onDocDown = (e: MouseEvent) => {
      if (!root.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocDown)

    return () => {
      observer.disconnect()
      document.removeEventListener('mousedown', onDocDown)
    }
  }, [t])

  const CustomPin = (name: string) => {
    return (
      <S.StoreLocatorCustomIconWrapper>
        <Icon name={name} useIconColor unlockedProportions />
      </S.StoreLocatorCustomIconWrapper>
    )
  }

  return (
    <Container>
      <S.StoreLocatorWrapper ref={locRootRef}>
        <S.StoreLocatorHeading>
          <Heading {...title} weight={'medium'} />
        </S.StoreLocatorHeading>
        <S.StoreLocatorLegend>
          <S.StoreLocatorLegendItem>
            {CustomPin('pin-main')}
            <Paragraph
              content={t('storeLocator.mainPinLabel')}
              typo={'paragraphMD'}
            />
          </S.StoreLocatorLegendItem>
          <S.StoreLocatorLegendItem>
            {CustomPin('pin-partner')}
            <Paragraph
              content={t('storeLocator.partnerPinLabel')}
              typo={'paragraphMD'}
            />
          </S.StoreLocatorLegendItem>
        </S.StoreLocatorLegend>
        <Locator
          {...config}
          mapId={mapId || config.mapId}
          localizationConfig={{
            lang: 'it',
            labels: {
              'Search.placeholder': t('storeLocator.searchPlaceholder'),
              'Search.geolocationButton': t('storeLocator.geolocationLabel'),
              'LocationList.noResults': t('storeLocator.noResultsLabel'),
              'LocationList.header': t.raw(
                'storeLocator.locationListHeaderLabel'
              ),
              'LocationList.locationDesignator': t.raw(
                'storeLocator.locationListHeaderType'
              ),
            },
          }}
          featureFlags={{
            withClusters: true,
            withCustomMarkers: true,
            withAreaSearch: false,
            withCustomInfoWindow: true,
          }}
          clusteringProvider="google"
          customMarkers={[
            {
              markerType: 'main',
              component: () => CustomPin('pin-main'),
            },
            {
              markerType: 'partner',
              component: () => CustomPin('pin-partner'),
            },
          ]}
          customZoomLevels={{
            INITIAL_ZOOM_LEVEL: 3,
          }}
          customInfoWindow={{
            component: InfoWindow,
          }}
        >
          <Search />
          <Map />
        </Locator>
      </S.StoreLocatorWrapper>
    </Container>
  )
}

export default StoreLocator
